import { ColumnTypes, FilterTypes } from "commons/table/Constants";
import { formatDateWithTime } from "services/MomentUtils";
import { TranscriptionConstants } from "lookup/TranscriptionConstants";

export const TRANSCRIBER_PROGRAM_CODE = {
  STD: "STD",
  DMR: "DMR",
};

export const TRANSCRIBER_FAX_PROGRAM_TYPE_LIST_COLUMNS = [
  {
    header: "Fax Program",
    accessor: "faxProgram",
    accessorKey: "faxProgram",
    columnType: ColumnTypes.Text,
    width: 300,
    enableSorting: true,
    enableColumnFilter: true,
    filterType: FilterTypes.SearchableInput,
  },
  {
    header: "Fax Count",
    accessor: "faxCount",
    accessorKey: "faxCount",
    columnType: ColumnTypes.Text,
    width: 100,
    enableSorting: true,
  },
  {
    header: "Received Date",
    accessor: "receivedDate",
    accessorKey: "receivedDate",
    columnType: ColumnTypes.Custom,
    width: 150,
    enableSorting: true,
    customComponent: (value, row) => {
      const faxCount = row.original.faxCount;
      if (faxCount === 0) {
        return <span>N/A</span>;
      }
      return <span>{formatDateWithTime(value)}</span>;
    },
  },
];

export const DMR_TRANSCRIBED_FAX_LIST_DEFAULT_SORTING = [
  { id: "generatedDate", desc: true, isDefault: true },
];

export const DMR_TRANSCRIBED_FAX_LIST_DEFAULT_FILTER = [];

export const TRANSCRIBER_LIST_ENDPOINT = "/transcriber/list";

export const buildTranscriberDetailsApiPath = (programCode, transcriptionId) =>
  `/transcriber/${encodeURIComponent(programCode.toLowerCase())}/details/${transcriptionId}`;

export const buildTranscriberPdfApiPath = (programCode, transcriptionId) =>
  `/pdf/prescriber-inbound-redacted-fax-info/${encodeURIComponent(programCode.toLowerCase())}/${transcriptionId}`;

export const TRANSCRIBER_PROGRAM_CONFIG = {
  [TRANSCRIBER_PROGRAM_CODE.STD]: {
    programCode: TRANSCRIBER_PROGRAM_CODE.STD,
    title: "Transcribed Fax List",
    documentColumnHeader: "Fax Document",
    documentColumnWidth: 400,
    trackIndexOnRowClick: true,
    getDetailsPath: (row) => `/transcription/${row.transcriptionInfoUuid}`,
    defaultSorting: TranscriptionConstants.DEFAULT_TRANSCRIBED_FAX_LIST_SORTING,
    defaultFilter: TranscriptionConstants.DEFAULT_TRANSCRIBED_FAX_LIST_FILTER,
  },
  [TRANSCRIBER_PROGRAM_CODE.DMR]: {
    programCode: TRANSCRIBER_PROGRAM_CODE.DMR,
    title: "DMR Transcribed Fax List",
    documentColumnHeader: "DMR Fax Document",
    documentColumnWidth: 350,
    trackIndexOnRowClick: false,
    getDetailsPath: (row) =>
      `/transcription/dmr/${row.programTranscriptionInfoUuid}`,
    defaultSorting: DMR_TRANSCRIBED_FAX_LIST_DEFAULT_SORTING,
    defaultFilter: DMR_TRANSCRIBED_FAX_LIST_DEFAULT_FILTER,
  },
};

export const getTranscriberProgramConfig = (programCode) =>
  TRANSCRIBER_PROGRAM_CONFIG[programCode] ?? null;

export const TRANSCRIBER_LIST_HIDDEN_COLUMNS = {
  [TRANSCRIBER_PROGRAM_CODE.STD]: ["referenceId", "ruleVersion", "appliedRule"],
  [TRANSCRIBER_PROGRAM_CODE.DMR]: [
    "associatedSwitchId",
    "customer",
    "client",
    "selected",
  ],
};

export const getTranscriberListHiddenColumns = (programCode) =>
  TRANSCRIBER_LIST_HIDDEN_COLUMNS[programCode] ?? [];

export const TRANSCRIBER_LIST_COLUMNS = (
  programConfig,
  { renderLastModifiedDateCol, renderEffectiveDateCol },
  hiddenColumns = []
) => {
  const allColumns = [
    {
      header: programConfig.documentColumnHeader,
      accessor: "faxFileKey",
      accessorKey: "faxFileKey",
      width: programConfig.documentColumnWidth,
      enableSorting: false,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Ref Id",
      accessor: "referenceId",
      accessorKey: "referenceId",
      width: 200,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Associated Switch",
      accessor: "associatedSwitchId",
      accessorKey: "associatedSwitchId",
      width: 200,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Customer",
      accessor: "customer",
      accessorKey: "customer",
      width: 100,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Client",
      accessor: "client",
      accessorKey: "client",
      width: 100,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Disposition",
      accessor: "disposition",
      accessorKey: "disposition",
      width: 200,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Model Name",
      accessor: "modelName",
      accessorKey: "modelName",
      width: 200,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Rule Version",
      accessor: "ruleVersion",
      accessorKey: "ruleVersion",
      width: 150,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Rule Number",
      accessor: "appliedRule",
      accessorKey: "appliedRule",
      width: 150,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Selected",
      accessor: "selected",
      accessorKey: "selected",
      width: 100,
      enableSorting: false,
      enableColumnFilter: true,
      columnType: ColumnTypes.Boolean,
      filterType: FilterTypes.SingleSelect,
      filterOptions: TranscriptionConstants.SELECTED_FIELD_OPTIONS,
      filterReturnKey: true,
    },
    {
      header: "Last Modified Date",
      accessor: "lastModifiedDate",
      accessorKey: "lastModifiedDate",
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => renderLastModifiedDateCol(value),
      filterType: FilterTypes.DateRangePicker,
      placeDatePickerAtEnd: true,
    },
    {
      header: "Received Date",
      accessor: "generatedDate",
      accessorKey: "generatedDate",
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => renderEffectiveDateCol(value),
      filterType: FilterTypes.DateRangePicker,
      placeDatePickerAtEnd: true,
    },
  ];

  return allColumns.filter((column) => !hiddenColumns.includes(column.accessorKey));
};

export const buildTranscriberProgramListColumns = (
  programConfig,
  columnRenderers
) =>
  TRANSCRIBER_LIST_COLUMNS(
    programConfig,
    columnRenderers,
    getTranscriberListHiddenColumns(programConfig.programCode)
  );
